import { Head, Link, useForm } from "@inertiajs/react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ArrowLeft, Edit } from "lucide-react"
import AppSidebarLayout from "@/layouts/app/app-sidebar-layout"
import { liabilitiesPath, liabilityPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Dashboard",
    href: "/dashboard",
  },
  {
    title: "Liabilities",
    href: liabilitiesPath(),
  },
  {
    title: "Edit Liability",
    href: "/liabilities/edit",
  },
]

interface LiabilityFormData {
  liability: {
    name: string
    category: string
    balance: number
    interest_rate: number | string
    minimum_payment: number | string
  }
}

interface LiabilitiesEditProps {
  liability: {
    id: number
    name: string
    category: string
    balance: number
    interest_rate: number | null
    minimum_payment: number | null
  }
  categories: string[]
  errors?: string[]
}

export default function LiabilitiesEdit({
  liability,
  categories,
  errors = [],
}: LiabilitiesEditProps) {
  const { data, setData, patch, processing } = useForm<LiabilityFormData>({
    liability: {
      name: liability.name,
      category: liability.category,
      balance: liability.balance,
      interest_rate: liability.interest_rate ?? "",
      minimum_payment: liability.minimum_payment ?? "",
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(liabilityPath(liability.id))
  }

  return (
    <AppSidebarLayout breadcrumbs={breadcrumbs}>
      <Head title="Edit Liability" />

      <div className="flex h-full flex-1 flex-col gap-6 overflow-x-auto rounded-xl p-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href={liabilitiesPath()}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Liabilities
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Edit Liability</h1>
            <p className="text-muted-foreground">
              Update your liability details
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5" />
                Liability Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={data.liability.name}
                    onChange={(e) =>
                      setData("liability", {
                        ...data.liability,
                        name: e.target.value,
                      })
                    }
                    required
                  />
                </div>

                {/* Category */}
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={data.liability.category}
                    onValueChange={(value) =>
                      setData("liability", {
                        ...data.liability,
                        category: value,
                      })
                    }
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Balance */}
                <div className="space-y-2">
                  <Label htmlFor="balance">Current Balance *</Label>
                  <div className="relative">
                    <div className="text-muted-foreground absolute top-2.5 left-3">
                      $
                    </div>
                    <Input
                      id="balance"
                      type="number"
                      step="0.01"
                      min="0"
                      className="pl-8"
                      value={data.liability.balance}
                      onChange={(e) =>
                        setData("liability", {
                          ...data.liability,
                          balance: parseFloat(e.target.value) || 0,
                        })
                      }
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Interest Rate */}
                  <div className="space-y-2">
                    <Label htmlFor="interest_rate">Interest Rate (APR %)</Label>
                    <Input
                      id="interest_rate"
                      type="number"
                      step="0.01"
                      min="0"
                      value={data.liability.interest_rate}
                      onChange={(e) =>
                        setData("liability", {
                          ...data.liability,
                          interest_rate: e.target.value,
                        })
                      }
                    />
                  </div>

                  {/* Minimum Payment */}
                  <div className="space-y-2">
                    <Label htmlFor="minimum_payment">
                      Min. Monthly Payment
                    </Label>
                    <Input
                      id="minimum_payment"
                      type="number"
                      step="0.01"
                      min="0"
                      value={data.liability.minimum_payment}
                      onChange={(e) =>
                        setData("liability", {
                          ...data.liability,
                          minimum_payment: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>

                {/* Error Messages */}
                {errors.length > 0 && (
                  <div className="bg-destructive/15 rounded-md p-4">
                    <div className="flex">
                      <div className="ml-3">
                        <h3 className="text-destructive text-sm font-medium">
                          There were errors with your submission:
                        </h3>
                        <div className="text-destructive mt-2 text-sm">
                          <ul role="list" className="list-disc space-y-1 pl-5">
                            {errors.map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="flex gap-4 pt-4">
                  <Button type="submit" disabled={processing}>
                    {processing ? "Updating..." : "Update Liability"}
                  </Button>
                  <Button variant="outline" asChild>
                    <Link href={liabilitiesPath()}>Cancel</Link>
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppSidebarLayout>
  )
}
